import React, { useState } from "react";
import PropTypes from "prop-types";
import { Star } from "lucide-react";

import { clx } from "../../../utils/Helpers";
import "./UIRating.scss";

const normalizeRatingValue = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCompactRating = (value) => {
  const num = normalizeRatingValue(value);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(1).replace(/\.0$/, "");
};

const displayCompactRating = (value, preserveValue) => {
  if (preserveValue) {
    if (value === null || value === undefined || value === "") return "0";
    return String(value);
  }
  return formatCompactRating(value);
};

const UIRating = ({
  value,
  onChange,
  max,
  className,
  variant,
  size,
  preserveValue,
}) => {
  const [hovered, setHovered] = useState(0);
  const isCompact = variant === "compact" || (!onChange && size === "small");
  const numericValue = normalizeRatingValue(value);

  if (isCompact) {
    return (
      <span className={clx("ui-rating", "ui-rating--compact", className)}>
        <span className="ui-rating__value">
          {displayCompactRating(value, preserveValue)}
        </span>
        <span
          className="ui-rating__star-display ui-rating__star-display--active"
          aria-hidden="true"
        >
          <Star />
        </span>
      </span>
    );
  }

  if (onChange) {
    const activeUpTo = hovered || value;
    return (
      <span
        className={clx("ui-rating", "ui-rating--interactive", className)}
        onMouseLeave={() => setHovered(0)}
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
          <button
            key={star}
            type="button"
            aria-label={`Rate ${star} of ${max}`}
            className={clx(
              "ui-rating__star-btn",
              star <= activeUpTo && "ui-rating__star-btn--active",
            )}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
          >
            <Star aria-hidden="true" />
          </button>
        ))}
      </span>
    );
  }

  return (
    <span className={clx("ui-rating", className)}>
      {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
        <span
          key={star}
          className={clx(
            "ui-rating__star-display",
            star <= numericValue && "ui-rating__star-display--active",
          )}
        >
          <Star aria-hidden="true" />
        </span>
      ))}
    </span>
  );
};

UIRating.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  max: PropTypes.number,
  className: PropTypes.string,
  variant: PropTypes.oneOf(["default", "compact"]),
  size: PropTypes.oneOf(["small", "medium"]),
  preserveValue: PropTypes.bool,
};

UIRating.defaultProps = {
  value: 0,
  onChange: undefined,
  max: 5,
  className: "",
  variant: "default",
  size: "medium",
  preserveValue: false,
};

export default UIRating;
